import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Wallet, 
  Upload, 
  Copy, 
  Check, 
  ArrowLeft, 
  Clock, 
  CheckCircle, 
  XCircle,
  Image as ImageIcon,
  Info,
  CreditCard
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { compressImage } from "../utils/imageCompressor";
import { PaymentDisabledBanner } from "@/components/PaymentGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bank account info
const BANK_INFO = {
  bank: "BRI",
  accountNo: "2292 0100 0104 504",
  accountName: "FIO ALGUSTI.AR"
};

export default function TopUpRequest() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [amount, setAmount] = useState("");
  const [paymentProof, setPaymentProof] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    checkAuth();
    fetchRequests();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("user_token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const response = await axios.get(`${API}/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch {
      // Do NOT auto-clear tokens on 401. User must click "Keluar" to log out.
      /* keep session */
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      const token = localStorage.getItem("user_token");
      const response = await axios.get(`${API}/user/topup-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data.requests || []);
    } catch (err) {
      console.error("Failed to fetch requests:", err);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(BANK_INFO.accountNo.replace(/\s/g, ""));
    setCopied(true);
    toast.success("Nomor rekening berhasil disalin");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("File harus berupa gambar");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ukuran file maksimal 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(URL.createObjectURL(file));
    };
    reader.readAsDataURL(file);
    
    // Compress and store
    compressImage(file, 800, 0.5).then(compressed => {
      setPaymentProof(compressed);
    });
  };

  const handleSubmit = async () => {
    const numAmount = parseInt(amount.replace(/\D/g, ""));
    
    if (!numAmount || numAmount < 50000 || numAmount > 1000000) {
      toast.error("Nominal harus antara Rp 50.000 - Rp 1.000.000");
      return;
    }

    if (!paymentProof) {
      toast.error("Silakan upload bukti transfer");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("user_token");
      await axios.post(
        `${API}/user/topup-request`,
        { amount: numAmount, payment_proof: paymentProof },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Permintaan top-up berhasil dikirim!");
      setAmount("");
      setPaymentProof(null);
      setPreviewUrl(null);
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengirim permintaan");
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (value) => {
    const num = value.replace(/\D/g, "");
    return num ? `Rp ${parseInt(num).toLocaleString("id-ID")}` : "";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const quickAmounts = [50000, 100000, 200000, 500000, 1000000];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-white">
        <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-cyan-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-sky-500 to-cyan-500 text-white px-4 py-6">
        <div className="max-w-lg mx-auto">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Kembali</span>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Wallet className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Tambah Saldo</h1>
              <p className="text-white/80 text-sm">Isi saldo untuk transaksi</p>
            </div>
          </div>
          {/* Current Balance */}
          <div className="mt-4 bg-white/10 rounded-xl p-4">
            <p className="text-white/70 text-sm">Saldo Saat Ini</p>
            <p className="text-2xl font-bold">
              Rp {(user?.balance || 0).toLocaleString("id-ID")}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
        <PaymentDisabledBanner />
        {/* Bank Info Card */}
        <Card className="overflow-hidden border-0 shadow-lg">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 text-white">
            <div className="flex items-center gap-3">
              <CreditCard className="w-6 h-6" />
              <span className="font-semibold">Transfer ke Rekening</span>
            </div>
          </div>
          <CardContent className="p-5">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Bank</p>
                  <p className="font-semibold text-lg">{BANK_INFO.bank}</p>
                </div>
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/6/68/BANK_BRI_logo.svg/200px-BANK_BRI_logo.svg.png" 
                  alt="BRI" 
                  className="h-8 object-contain"
                />
              </div>
              <div>
                <p className="text-sm text-slate-500">Nomor Rekening</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="font-mono text-xl font-bold text-slate-800">
                    {BANK_INFO.accountNo}
                  </p>
                  <button
                    onClick={copyToClipboard}
                    className={`p-2 rounded-lg transition-colors ${
                      copied 
                        ? "bg-emerald-100 text-emerald-600" 
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                    data-testid="copy-account-btn"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <p className="text-sm text-slate-500">Atas Nama</p>
                <p className="font-semibold text-slate-800">{BANK_INFO.accountName}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Amount Input */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-5">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Nominal Top-Up
            </label>
            <Input
              type="text"
              value={amount}
              onChange={(e) => setAmount(formatCurrency(e.target.value))}
              placeholder="Masukkan nominal"
              className="h-12 text-lg font-medium"
              data-testid="amount-input"
            />
            <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
              <Info className="w-3 h-3" />
              <span>Min: Rp 50.000 - Max: Rp 1.000.000</span>
            </div>
            
            {/* Quick Amount Buttons */}
            <div className="flex flex-wrap gap-2 mt-4">
              {quickAmounts.map((qAmount) => (
                <button
                  key={qAmount}
                  onClick={() => setAmount(`Rp ${qAmount.toLocaleString("id-ID")}`)}
                  className="px-3 py-2 text-sm font-medium bg-slate-100 hover:bg-sky-100 hover:text-sky-700 rounded-lg transition-colors"
                  data-testid={`quick-amount-${qAmount}`}
                >
                  {qAmount >= 1000000 
                    ? `${qAmount / 1000000}jt` 
                    : `${qAmount / 1000}rb`}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Upload Proof */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-5">
            <label className="block text-sm font-medium text-slate-700 mb-3">
              Bukti Transfer
            </label>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />

            {previewUrl ? (
              <div className="relative">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full h-48 object-cover rounded-xl"
                />
                <button
                  onClick={() => {
                    setPaymentProof(null);
                    setPreviewUrl(null);
                  }}
                  className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                  data-testid="remove-image-btn"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-40 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center gap-3 hover:border-sky-400 hover:bg-sky-50 transition-colors"
                data-testid="upload-btn"
              >
                <div className="w-14 h-14 bg-sky-100 rounded-full flex items-center justify-center">
                  <Upload className="w-6 h-6 text-sky-600" />
                </div>
                <div className="text-center">
                  <p className="font-medium text-slate-700">Upload Bukti Transfer</p>
                  <p className="text-sm text-slate-500">JPG, PNG maks 5MB</p>
                </div>
              </button>
            )}
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={submitting || !amount || !paymentProof}
          className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600"
          data-testid="submit-topup-btn"
        >
          {submitting ? (
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Mengirim...</span>
            </div>
          ) : (
            "Kirim Permintaan Top-Up"
          )}
        </Button>

        {/* Request History */}
        {requests.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">
              Riwayat Permintaan
            </h2>
            <div className="space-y-3">
              <AnimatePresence>
                {requests.map((req) => (
                  <motion.div
                    key={req.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl p-4 shadow-md border border-slate-100"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-slate-800">
                          Rp {req.amount?.toLocaleString("id-ID")}
                        </p>
                        <p className="text-sm text-slate-500">
                          {formatDate(req.created_at)}
                        </p>
                      </div>
                      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
                        req.status === "pending" 
                          ? "bg-amber-100 text-amber-700"
                          : req.status === "approved"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-red-100 text-red-700"
                      }`}>
                        {req.status === "pending" && <Clock className="w-4 h-4" />}
                        {req.status === "approved" && <CheckCircle className="w-4 h-4" />}
                        {req.status === "rejected" && <XCircle className="w-4 h-4" />}
                        <span>
                          {req.status === "pending" ? "Menunggu" :
                           req.status === "approved" ? "Disetujui" : "Ditolak"}
                        </span>
                      </div>
                    </div>
                    {req.note && (
                      <p className="text-sm text-slate-500 mt-2 bg-slate-50 rounded-lg p-2">
                        Catatan: {req.note}
                      </p>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
